import { PointOfInterest } from '@/__generated__/graphql';
import { useFeatureFlag } from '@/hooks';
import { abyssColors } from '@/theme/colors';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { Close, Link } from '@mui/icons-material';
import { ReviewStatus } from '../../ReviewStatus';
import { ReviewAction } from '../../ReviewAction';
import { ActionMenu } from '../../ActionMenu';
import { usePoiTemplateFields } from '../../../../hooks/usePoiTemplateFields';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { CopyImageUrl } from './styles';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { LocationTag, VIEW_IN_3D_LABEL, VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useReviewIn3D } from '@/components/Analysis/ReviewScreen/hooks/useReviewIn3D';

type ReviewStatusProps = {
  onClose: () => void;
  currentPoi: PointOfInterest;
};

export const DialogHeader = ({ currentPoi, onClose }: ReviewStatusProps) => {
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const isAPIInspector = useFeatureFlag('api-inspector');
  const { showSnackBar } = useSnackBarMessage({});
  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const { templateOfSelectedPoi } = usePoiTemplateFields({
    data: currentPoi || ({} as PointOfInterest),
  });
  const { handleViewIn3D } = useReviewIn3D({ pointOfInterest: currentPoi });

  const handleCopyImageUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('selectedAssemblyName', selectedAssemblyName || '');
    url.searchParams.set('selectedAssemblyId', selectedAssemblyId || '');
    navigator.clipboard.writeText(url.toString());
    showSnackBar('URL copied to clipboard', 'success');
  };

  return (
    <Stack
      direction="row"
      justifyContent="end"
      alignItems="center"
      sx={{ padding: '26px 23px 0 23px' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CopyImageUrl
          onClick={handleCopyImageUrl}
          underline="none"
          id={`${LocationTag.ReviewPanelPoiModal}-copy-image-link`}
        >
          <Link sx={{ transform: 'rotate(-45deg)', fontSize: '1.8rem' }} />
          Copy Image Link
        </CopyImageUrl>
        <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="left" arrow>
          <Button
            variant="text"
            size="small"
            startIcon={<AbyssIcon iconName="cube-secondary" size="md" />}
            onClick={(event) => handleViewIn3D(event)}
            id={`${LocationTag.ReviewPanelPoiModal}-view-in-3d-button`}
          >
            {VIEW_IN_3D_LABEL}
          </Button>
        </Tooltip>
        {currentPoi && (
          <>
            {isAITEngineer && (
              <ReviewStatus
                pointOfInterest={currentPoi}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={LocationTag.ReviewPanelPoiModal}
              />
            )}
            {isAPIInspector && (
              <ReviewAction
                pointOfInterest={currentPoi}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={LocationTag.ReviewPanelPoiModal}
              />
            )}
            <ActionMenu pointOfInterest={currentPoi} location={LocationTag.ReviewPanelPoiModal} />
          </>
        )}
        <IconButton
          onClick={onClose}
          size="small"
          id={`${LocationTag.ReviewPanelPoiModal}-close-button`}
        >
          <Close sx={{ color: abyssColors.primary[500] }} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
